/**
 * AppellationField — champ éditable au clic avec autocomplétion depuis l'index des
 * appellations viticoles (backend). En choisissant une appellation, on peut pré-remplir
 * la région/vignoble (via onPick) ; AppellationHint rappelle les cépages classiques.
 */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Check, Plus, Sparkles } from 'lucide-react';
import { vignoblesApi } from '../../services/api';
import { AppellationDrawer } from './AppellationDrawer';

export function AppellationField({ value, onSave, onPick, testId = 'appellation', inputClassName = '', displayClassName = '' }) {
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState(value ?? '');
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => setQ(value ?? ''), [value]);

  const { data: matches = [] } = useQuery({
    queryKey: ['appellations', q],
    queryFn: () => vignoblesApi.appellations(q),
    enabled: editing,
  });

  const flash = () => { setSaved(true); setTimeout(() => setSaved(false), 1500); };
  const commit = async (name, meta) => {
    setEditing(false); setOpen(false);
    if ((name ?? '') !== (value ?? '')) { await onSave(name); flash(); }
    if (meta && onPick) onPick(meta);
  };

  if (!editing) {
    return (
      <span role="button" tabIndex={0} data-testid={`${testId}-display`}
        onClick={() => { setEditing(true); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { setEditing(true); setOpen(true); } }}
        className={`group inline-flex items-start gap-1 cursor-text rounded px-0.5 hover:outline hover:outline-1 hover:outline-dashed hover:outline-gold ${displayClassName}`}>
        <span className={!value ? 'text-n500' : ''}>{value || 'Appellation'}</span>
        <Pencil className="w-3 h-3 mt-1 shrink-0 opacity-0 group-hover:opacity-60 text-gold700" />
        {saved && <Check className="w-3 h-3 mt-1 text-gold700" />}
      </span>
    );
  }

  return (
    <span className="relative inline-block">
      <input autoFocus value={q} className={`vd-input ${inputClassName}`} data-testid={`${testId}-input`}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => { if (!drawerOpen) commit(q); }, 160)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(q); if (e.key === 'Escape') { setQ(value ?? ''); setEditing(false); setOpen(false); } }} />
      {open && (
        <div className="absolute z-30 mt-1 bg-paper border border-hair rounded shadow-md w-72 max-h-56 overflow-auto" data-testid={`${testId}-menu`}>
          {matches.slice(0, 15).map((m) => (
            <button key={m.appellation} data-testid={`${testId}-opt-${m.appellation}`}
              onMouseDown={() => commit(m.appellation, { vignoble: m.vignoble, cepages: m.entries.map((e) => e.cepages).filter(Boolean) })}
              className="block w-full text-left px-2.5 py-1.5 text-[13px] hover:bg-surface">
              <span className="font-medium">{m.appellation}</span>
              {m.vignoble && <span className="text-n500"> · {m.vignoble}</span>}
            </button>
          ))}
          <button data-testid={`${testId}-create`}
            onMouseDown={(e) => { e.preventDefault(); setDrawerOpen(true); setOpen(false); }}
            className="flex items-center gap-1 w-full text-left px-2.5 py-1.5 text-[13px] text-gold700 hover:bg-surface border-t border-hair">
            <Plus className="w-3.5 h-3.5" /> Créer / compléter{q.trim() ? ` « ${q.trim()} »` : ' une appellation'}
          </button>
        </div>
      )}
      <AppellationDrawer open={drawerOpen} initialName={q}
        onClose={() => { setDrawerOpen(false); setEditing(false); }}
        onSaved={(saved) => {
          commit(saved.appellation, { vignoble: saved.vignoble, cepages: (saved.entries || []).map((e) => e.cepages).filter(Boolean) });
        }} />
    </span>
  );
}

/** Rappel de l'appellation courante (sous le champ) : cépages classiques, sous-région, remarques. */
export function AppellationHint({ value }) {
  const { data: matches = [] } = useQuery({
    queryKey: ['appellations', value],
    queryFn: () => vignoblesApi.appellations(value),
    enabled: !!value,
  });
  if (!value) return null;
  const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const hit = matches.find((m) => norm(m.appellation) === norm(value));
  if (!hit) return null;
  const cepageEntries = (hit.entries || []).filter((e) => e.cepages);
  const remarkEntries = (hit.entries || []).filter((e) => e.remarques);
  if (!cepageEntries.length && !hit.sous_region && !remarkEntries.length) return null;
  return (
    <div className="text-[11.5px] text-n500 mt-1 leading-snug space-y-0.5" data-testid="appellation-hint">
      {cepageEntries.length > 0 && (
        <div data-testid="appellation-hint-cepages">
          <span className="text-n600">Cépages classiques :</span>{' '}
          {cepageEntries.map((e, i) => (
            <span key={i}>{e.couleur ? `${e.couleur.split(',')[0]} — ` : ''}{e.cepages}{i < cepageEntries.length - 1 ? ' · ' : ''}</span>
          ))}
        </div>
      )}
      {hit.sous_region && (
        <div data-testid="appellation-hint-sousregion">
          <span className="text-n600">Sous-région :</span> {hit.sous_region}
        </div>
      )}
      {remarkEntries.length > 0 && (
        <div data-testid="appellation-hint-remarques">
          <span className="text-n600">Remarque :</span>{' '}
          {remarkEntries.map((e, i) => (
            <span key={i}>{e.couleur ? `${e.couleur.split(',')[0]} — ` : ''}{e.remarques}{i < remarkEntries.length - 1 ? ' · ' : ''}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Extrait des noms de cépages depuis une chaîne libre d'appellation (best-effort). */
export function parseCepages(s) {
  return (s || '')
    .replace(/voir\s+[\w\-']+/gi, ' ')
    .replace(/(c[ée]pages?|principaux|principal|accessoires?|associ[ée]s?|autoris[ée]s?|assemblages?|majoritaires?|compl[ée]mentaires?)\s*:?/gi, ' ')
    .replace(/(blancs?|rouges?|ros[ée]s?|noirs?)\s*:/gi, ' ')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[≥≤<>%0-9.…*]/g, ' ')
    .split(/[,;/:+]|\s+et\s+|\s{2,}/)
    .map((p) => p.replace(/^[\s\u00a0:+\-–—]+|[\s\u00a0:+\-–—]+$/g, ''))
    .filter((t) => t.length >= 2)
    .map((t) => t.charAt(0).toUpperCase() + t.slice(1));
}

/** Bouton « Utiliser les cépages classiques » : pré-remplit depuis l'appellation (couleur assortie). */
export function ClassicCepagesButton({ appellation, color, onApply, testId = 'use-classic-cepages' }) {
  const { data: matches = [] } = useQuery({
    queryKey: ['appellations', appellation],
    queryFn: () => vignoblesApi.appellations(appellation),
    enabled: !!appellation,
  });
  if (!appellation) return null;
  const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const hit = matches.find((m) => norm(m.appellation) === norm(appellation));
  if (!hit) return null;
  const entries = hit.entries || [];
  const byColor = color ? entries.find((e) => e.cepages && norm(e.couleur).includes(norm(color))) : null;
  const chosen = byColor || entries.find((e) => e.cepages);
  const names = chosen ? parseCepages(chosen.cepages) : [];
  if (!names.length) return null;
  return (
    <div className="mt-1.5" data-testid={`${testId}-wrap`}>
      <button type="button" data-testid={testId}
        onClick={() => onApply(names.map((nom) => ({ nom, pct: null })))}
        className="inline-flex items-center gap-1 text-[12px] text-gold700 hover:underline">
        <Sparkles className="w-3.5 h-3.5" /> Utiliser les cépages classiques
      </button>
      <span className="text-[11.5px] text-n500 ml-1">({names.join(', ')})</span>
    </div>
  );
}
